import { useNavigate } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import type { ConfirmSignInOutput, SignInOutput } from "aws-amplify/auth";

import {
  identityProviderMapError,
  isIdentityProviderError,
} from "../../identity-provider-error";
import { Auth } from "../../isomorphic-auth/isomorphic-auth";
import { authenticationQueryKey } from "../../queries/authentication-query-keys";
import { TOTP_APP_NAME } from "../../two-factor";

import type {
  ConfirmResetPasswordProps,
  SignInOutcome,
  SignInProps,
  UpdatePasswordProps,
} from "./use-authentication.types";

export const useAuthentication = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const enterAdmin = () =>
    navigate(`/home${location.search}`, {
      state: { fromLogin: true },
    });

  // The step Cognito wants next (OMG-2777). In the admin pool a first sign-in
  // goes password, new password, authenticator setup; every later one
  // password, then the authenticator code. Any other step is an error, never
  // a way in: entering the admin without a session only bounces back.
  const outcomeOf = (
    output: SignInOutput | ConfirmSignInOutput,
    email: string,
  ): SignInOutcome => {
    const { nextStep } = output;
    if (output.isSignedIn || nextStep?.signInStep === "DONE") {
      enterAdmin();
      return { step: "signed-in" };
    }
    switch (nextStep?.signInStep) {
      case "CONFIRM_SIGN_IN_WITH_TOTP_CODE":
        return { step: "totp" };
      case "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED":
        return { step: "new-password" };
      case "CONTINUE_SIGN_IN_WITH_TOTP_SETUP": {
        const details = nextStep.totpSetupDetails;
        return {
          step: "totp-setup",
          setup: {
            secret: details.sharedSecret,
            uri: details.getSetupUri(TOTP_APP_NAME, email).toString(),
          },
        };
      }
      default:
        throw new Error(`Unexpected sign-in step ${nextStep?.signInStep}`);
    }
  };

  const signIn = async ({
    email,
    password,
  }: SignInProps): Promise<SignInOutcome> => {
    try {
      const result = await Auth.signIn({
        username: email.toLowerCase(),
        password,
      });

      if (result.success) {
        return outcomeOf(result.data, email.toLowerCase());
      }

      throw result.error;
    } catch (error) {
      if (isIdentityProviderError(error)) {
        throw identityProviderMapError(error);
      }
      throw error;
    }
  };

  // Answers the step signIn (or the previous call) returned: the
  // authenticator code, the new password, or the first code from a freshly
  // scanned authenticator. `email` labels the account in the authenticator
  // app if Cognito asks for setup next.
  const continueSignIn = async (
    challengeResponse: string,
    email: string,
  ): Promise<SignInOutcome> => {
    try {
      const result = await Auth.confirmSignIn({ challengeResponse });

      if (result.success) {
        return outcomeOf(result.data, email.toLowerCase());
      }

      throw result.error;
    } catch (error) {
      if (isIdentityProviderError(error)) {
        throw identityProviderMapError(error);
      }
      throw error;
    }
  };

  const signOut = async () => {
    try {
      const result = await Auth.signOut();

      if (result.success) {
        queryClient.removeQueries({
          queryKey: authenticationQueryKey.authentication(),
        });
        return;
      }

      throw result.error;
    } catch (error) {
      if (isIdentityProviderError(error)) {
        throw identityProviderMapError(error);
      }
      throw error;
    }
  };

  const resetPassword = async (email: string) => {
    try {
      const result = await Auth.resetPassword({
        username: email.toLowerCase(),
      });

      if (result.success) {
        return;
      }

      throw result.error;
    } catch (error) {
      console.log(error);
      if (isIdentityProviderError(error)) {
        throw identityProviderMapError(error);
      }
      throw error;
    }
  };

  const confirmResetPassword = async ({
    email,
    confirmationCode,
    newPassword,
  }: ConfirmResetPasswordProps) => {
    try {
      const result = await Auth.confirmResetPassword({
        username: email.toLowerCase(),
        confirmationCode,
        newPassword,
      });

      if (result.success) {
        return;
      }

      throw result.error;
    } catch (error) {
      if (isIdentityProviderError(error)) {
        throw identityProviderMapError(error);
      }
      throw error;
    }
  };

  const updatePassword = async (props: UpdatePasswordProps) => {
    try {
      const result = await Auth.updatePassword(props);

      if (result.success) {
        return;
      }

      throw result.error;
    } catch (error) {
      if (isIdentityProviderError(error)) {
        throw identityProviderMapError(error);
      }
      throw error;
    }
  };

  return {
    signIn,
    continueSignIn,
    signOut,
    resetPassword,
    confirmResetPassword,
    updatePassword,
  };
};
